import React, { useState } from "react";
import {
  CalendarDays,
  Download,
  FileText,
  Loader2,
  MapPin,
  Video,
} from "lucide-react";
import { Link } from "react-router-dom";

import axiosInstance from "@/api/axiosInstance";
import { toast } from "@/hooks/useToast";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { groupSessionDays } from "@/modules/eventRegistration/shared/utils/groupSessionDays";

import type {
  CompanyEventRegistrationEnrollee,
  CompanyEventRegistrationItem,
} from "../../api/listCompanyEventRegistrations";

function formatTimeRange(
  startDate: string | null,
  endDate: string | null,
  timezoneShortName: string | null,
  timezoneIdentifier: string | null,
): string {
  if (!startDate) return "Date TBD";
  const tz = timezoneIdentifier ?? undefined;
  const start = new Date(startDate);
  const startTime = start.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });
  let result = startTime;
  if (endDate) {
    const end = new Date(endDate);
    const endTime = end.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
    });
    result += ` – ${endTime}`;
  }
  if (timezoneShortName) result += ` ${timezoneShortName}`;
  return result;
}

function formatVenue(
  venueName: string | null,
  venueCity: string | null,
  venueState: string | null,
): string | null {
  if (!venueName && !venueCity) return null;
  const parts: string[] = [];
  if (venueName) parts.push(venueName);
  const loc = [venueCity, venueState].filter(Boolean).join(", ");
  if (loc) parts.push(loc);
  return parts.join(" · ");
}

function DateTile({
  startDate,
  timezoneIdentifier,
  dim,
}: {
  startDate: string | null;
  timezoneIdentifier: string | null;
  dim: boolean;
}) {
  if (!startDate) {
    return (
      <div
        className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center bg-gray-100 dark:bg-slate-700/50 flex-shrink-0 ${
          dim ? "opacity-60" : ""
        }`}
      >
        <CalendarDays className="h-5 w-5 text-gray-400 dark:text-gray-500" />
        <span className="mt-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
          TBD
        </span>
      </div>
    );
  }
  const tz = timezoneIdentifier ?? undefined;
  const d = new Date(startDate);
  const month = d
    .toLocaleDateString("en-US", { month: "short", timeZone: tz })
    .toUpperCase();
  const day = d.toLocaleDateString("en-US", { day: "numeric", timeZone: tz });
  const weekday = d
    .toLocaleDateString("en-US", { weekday: "short", timeZone: tz })
    .toUpperCase();
  return (
    <div
      className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center bg-gray-50 dark:bg-slate-900/40 border border-gray-200 dark:border-slate-700 flex-shrink-0 ${
        dim ? "opacity-60" : ""
      }`}
    >
      <span className="text-[10px] uppercase tracking-wider font-medium text-gray-500 dark:text-gray-400">
        {month}
      </span>
      <span className="text-2xl font-light leading-none text-gray-900 dark:text-white">
        {day}
      </span>
      <span className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 mt-0.5">
        {weekday}
      </span>
    </div>
  );
}

function statusBadge(e: CompanyEventRegistrationEnrollee) {
  if (e.isRefunded) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Refunded
      </span>
    );
  }
  if (e.isCancelled) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Cancelled
      </span>
    );
  }
  if (e.isWaitlisted) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
        Waitlisted{e.waitlistPosition ? ` · #${e.waitlistPosition}` : ""}
      </span>
    );
  }
  if (e.usedVoucher) {
    return (
      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        Voucher
      </span>
    );
  }
  return null;
}

function isWithinCancellationWindow(startDate: string | null): boolean {
  if (!startDate) return false;
  const eventStart = new Date(startDate);
  const fourteenDaysFromNow = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
  return eventStart >= fourteenDaysFromNow;
}

function EnrolleeRow({
  enrollee,
  startDate,
  allowCancel,
  onCancelSuccess,
}: {
  enrollee: CompanyEventRegistrationEnrollee;
  startDate: string | null;
  allowCancel: boolean;
  onCancelSuccess: () => void;
}) {
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [reason, setReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const withinWindow = isWithinCancellationWindow(startDate);
  const canCancel =
    allowCancel && !enrollee.isCancelled && !enrollee.isRefunded;

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axiosInstance.post(
        `/api/private/enrollments/${enrollee.enrollmentId}/cancel`,
        { reason },
      );
      toast({
        title: "Registration cancelled",
        description: `${enrollee.firstName} ${enrollee.lastName}'s registration has been cancelled.`,
      });
      setShowCancelForm(false);
      setReason("");
      onCancelSuccess();
    } catch (err: any) {
      toast({
        title: "Error",
        description:
          err?.response?.data?.message || "Failed to cancel registration.",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-gray-900 dark:text-white truncate">
            {enrollee.firstName} {enrollee.lastName}
          </p>
          {enrollee.email && (
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {enrollee.email}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {enrollee.seatPrice && !enrollee.usedVoucher && (
            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
              ${enrollee.seatPrice}
            </span>
          )}
          {statusBadge(enrollee)}
        </div>
      </div>

      {canCancel && !showCancelForm && (
        <button
          className="mt-1 text-xs text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          onClick={() => setShowCancelForm(true)}
          type="button"
        >
          Cancel registration
        </button>
      )}

      {showCancelForm && (
        <div className="mt-2">
          {!withinWindow ? (
            <div className="p-3 rounded-md border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 mb-2">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                Cancellations within 14 days of the event start date must be
                handled by the training team.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                Please contact{" "}
                <span className="font-medium">training@mycertainpath.com</span>{" "}
                or call <span className="font-medium">945-270-8056</span>.
              </p>
              <div className="mt-2 flex justify-end">
                <Button
                  className="h-7 text-xs"
                  onClick={() => setShowCancelForm(false)}
                  size="sm"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Textarea
                className="mb-2"
                maxLength={255}
                onChange={(e) => setReason(e.target.value.slice(0, 255))}
                placeholder="Reason for cancellation..."
                rows={2}
                value={reason}
              />
              <div className="flex justify-end gap-2">
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling}
                  onClick={() => {
                    setShowCancelForm(false);
                    setReason("");
                  }}
                  size="sm"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="h-7 text-xs"
                  disabled={cancelling || !reason.trim()}
                  onClick={handleCancel}
                  size="sm"
                >
                  {cancelling && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  Confirm
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function CompanyEventRegistrationRow({
  registration,
  variant,
  onCancelSuccess,
}: {
  registration: CompanyEventRegistrationItem;
  variant: "active" | "past";
  onCancelSuccess: () => void;
}) {
  const isPast = variant === "past";
  // Session-level join link and materials should disappear once no one from the
  // company still holds an active seat (all enrollments cancelled/refunded).
  const hasActiveEnrollment = registration.enrollments.some(
    (e) => !e.isCancelled && !e.isRefunded,
  );
  const time = formatTimeRange(
    registration.startDate,
    registration.endDate,
    registration.timezoneShortName,
    registration.timezoneIdentifier,
  );
  // Broken-out session days render as stacked "date · time" lines; legacy
  // sessions fall back to the single time-only line below.
  const sessionDayGroups = groupSessionDays(
    registration.sessionDays,
    registration.timezoneIdentifier,
    registration.timezoneShortName,
  );
  const venue = formatVenue(
    registration.venueName,
    registration.venueCity,
    registration.venueState,
  );

  return (
    <div
      className={`bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4 ${
        isPast ? "opacity-90" : ""
      }`}
    >
      <div className="flex gap-4">
        <DateTile
          dim={isPast}
          startDate={registration.startDate}
          timezoneIdentifier={registration.timezoneIdentifier}
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3
                className={`text-base font-normal truncate ${
                  isPast
                    ? "text-gray-600 dark:text-gray-300"
                    : "text-gray-900 dark:text-white"
                }`}
              >
                {registration.eventName}
              </h3>
              {registration.sessionName &&
                registration.sessionName !== registration.eventName && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {registration.sessionName}
                  </p>
                )}
            </div>
            {registration.isSessionCancelled && (
              <span className="flex-shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
                Session Cancelled
              </span>
            )}
          </div>

          <div className="mt-1.5 space-y-0.5">
            {sessionDayGroups.length > 0 ? (
              sessionDayGroups.map((group, i) => (
                <p
                  className="text-sm text-gray-500 dark:text-gray-400"
                  key={`${group.dateLabel}-${i}`}
                >
                  {group.dateLabel} · {group.timeLabel}
                </p>
              ))
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">{time}</p>
            )}
            {venue && (
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="truncate">{venue}</span>
              </p>
            )}
            {registration.virtualLink && hasActiveEnrollment && (
              <p className="text-sm flex items-center gap-1.5">
                <Video className="h-3.5 w-3.5 flex-shrink-0 text-gray-500 dark:text-gray-400" />
                <a
                  className="text-blue-600 dark:text-blue-400 underline truncate hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                  href={registration.virtualLink}
                  rel="noreferrer"
                  target="_blank"
                >
                  Join link
                </a>
              </p>
            )}
            {hasActiveEnrollment &&
              registration.attachments &&
              registration.attachments.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5 flex-shrink-0" />
                    Materials
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {registration.attachments.map((file) => (
                      <li key={file.fileUuid}>
                        <a
                          className="text-sm text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 transition-colors inline-flex items-center gap-1.5"
                          href={file.downloadUrl ?? "#"}
                          rel="noreferrer"
                          target="_blank"
                        >
                          <Download className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">
                            {file.name ?? "Download"}
                          </span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              {registration.enrollments.length}{" "}
              {registration.enrollments.length === 1 ? "enrollee" : "enrollees"}{" "}
              from your company
            </p>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-slate-700/50 divide-y divide-gray-100 dark:divide-slate-700/50">
        {registration.enrollments.map((enrollee) => (
          <EnrolleeRow
            allowCancel={!isPast}
            enrollee={enrollee}
            key={enrollee.enrollmentId}
            onCancelSuccess={onCancelSuccess}
            startDate={registration.startDate}
          />
        ))}
      </div>

      {registration.eventUuid && (
        <div className="mt-3 flex justify-end">
          <Button asChild size="sm" variant="ghost">
            <Link to={`/event-registration/events/${registration.eventUuid}`}>
              View event details
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
}
